import { pinyin } from "pinyin-pro";

export type PriorityKey = "sound" | "glyph" | "imagery" | "distinction";
export type ImageryTag = "自然" | "学识" | "温柔" | "勇气" | "开阔" | "笃定";

export interface FamilyVote {
  role: string;
  favorite: string;
  reservation: string;
}

export interface SurveyInput {
  surname: string;
  candidates: string[];
  votes: FamilyVote[];
  priorities: Record<PriorityKey, number>;
  imagery: ImageryTag[];
  forbiddenChars: string;
  preferredChars: string;
  rareTolerance: "low" | "medium" | "high";
  duplicateSensitivity: "low" | "medium" | "high";
  riskStrictness: "low" | "medium" | "high";
}

interface HanziMeta {
  strokes: number;
  tags: ImageryTag[];
  familiarity: number;
}

const H: Record<string, HanziMeta> = {
  安: { strokes: 6, tags: ["温柔", "笃定"], familiarity: 92 },
  予: { strokes: 4, tags: ["温柔", "开阔"], familiarity: 78 },
  知: { strokes: 8, tags: ["学识", "笃定"], familiarity: 86 },
  夏: { strokes: 10, tags: ["自然", "开阔"], familiarity: 88 },
  清: { strokes: 11, tags: ["自然", "笃定"], familiarity: 91 },
  和: { strokes: 8, tags: ["温柔", "笃定"], familiarity: 94 },
  砚: { strokes: 9, tags: ["学识", "笃定"], familiarity: 63 },
  舟: { strokes: 6, tags: ["自然", "开阔"], familiarity: 80 },
  云: { strokes: 4, tags: ["自然", "开阔"], familiarity: 92 },
  川: { strokes: 3, tags: ["自然", "开阔"], familiarity: 89 },
  山: { strokes: 3, tags: ["自然", "笃定"], familiarity: 95 },
  海: { strokes: 10, tags: ["自然", "开阔"], familiarity: 94 },
  星: { strokes: 9, tags: ["自然", "开阔"], familiarity: 93 },
  月: { strokes: 4, tags: ["自然", "温柔"], familiarity: 96 },
  辰: { strokes: 7, tags: ["自然", "开阔"], familiarity: 92 },
  晨: { strokes: 11, tags: ["自然", "开阔"], familiarity: 94 },
  曦: { strokes: 20, tags: ["自然", "开阔"], familiarity: 58 },
  雨: { strokes: 8, tags: ["自然", "温柔"], familiarity: 94 },
  雪: { strokes: 11, tags: ["自然", "温柔"], familiarity: 92 },
  风: { strokes: 4, tags: ["自然", "开阔"], familiarity: 94 },
  林: { strokes: 8, tags: ["自然", "笃定"], familiarity: 96 },
  森: { strokes: 12, tags: ["自然", "笃定"], familiarity: 90 },
  木: { strokes: 4, tags: ["自然", "笃定"], familiarity: 92 },
  禾: { strokes: 5, tags: ["自然", "温柔"], familiarity: 84 },
  竹: { strokes: 6, tags: ["自然", "笃定"], familiarity: 84 },
  松: { strokes: 8, tags: ["自然", "笃定"], familiarity: 90 },
  柏: { strokes: 9, tags: ["自然", "笃定"], familiarity: 87 },
  桐: { strokes: 10, tags: ["自然", "温柔"], familiarity: 83 },
  棠: { strokes: 12, tags: ["自然", "温柔"], familiarity: 72 },
  沐: { strokes: 7, tags: ["自然", "温柔"], familiarity: 85 },
  泽: { strokes: 8, tags: ["自然", "温柔"], familiarity: 91 },
  涵: { strokes: 11, tags: ["自然", "学识"], familiarity: 91 },
  澄: { strokes: 15, tags: ["自然", "笃定"], familiarity: 74 },
  溪: { strokes: 13, tags: ["自然", "温柔"], familiarity: 83 },
  汐: { strokes: 6, tags: ["自然", "温柔"], familiarity: 78 },
  渊: { strokes: 11, tags: ["学识", "笃定"], familiarity: 73 },
  润: { strokes: 10, tags: ["自然", "温柔"], familiarity: 88 },
  若: { strokes: 8, tags: ["温柔", "开阔"], familiarity: 89 },
  宁: { strokes: 5, tags: ["温柔", "笃定"], familiarity: 94 },
  静: { strokes: 14, tags: ["温柔", "笃定"], familiarity: 93 },
  柔: { strokes: 9, tags: ["温柔"], familiarity: 88 },
  暖: { strokes: 13, tags: ["温柔"], familiarity: 76 },
  悦: { strokes: 10, tags: ["温柔", "开阔"], familiarity: 92 },
  欣: { strokes: 8, tags: ["温柔", "开阔"], familiarity: 94 },
  乐: { strokes: 5, tags: ["温柔", "开阔"], familiarity: 97 },
  嘉: { strokes: 14, tags: ["温柔", "笃定"], familiarity: 94 },
  瑞: { strokes: 13, tags: ["笃定"], familiarity: 93 },
  瑾: { strokes: 15, tags: ["温柔", "笃定"], familiarity: 80 },
  瑜: { strokes: 13, tags: ["温柔", "笃定"], familiarity: 83 },
  玉: { strokes: 5, tags: ["温柔", "笃定"], familiarity: 96 },
  一: { strokes: 1, tags: ["笃定"], familiarity: 99 },
  文: { strokes: 4, tags: ["学识"], familiarity: 98 },
  书: { strokes: 4, tags: ["学识"], familiarity: 95 },
  墨: { strokes: 15, tags: ["学识", "笃定"], familiarity: 78 },
  言: { strokes: 7, tags: ["学识", "勇气"], familiarity: 91 },
  语: { strokes: 9, tags: ["学识", "温柔"], familiarity: 93 },
  思: { strokes: 9, tags: ["学识", "笃定"], familiarity: 95 },
  哲: { strokes: 10, tags: ["学识", "笃定"], familiarity: 90 },
  睿: { strokes: 14, tags: ["学识", "笃定"], familiarity: 86 },
  博: { strokes: 12, tags: ["学识", "开阔"], familiarity: 94 },
  学: { strokes: 8, tags: ["学识"], familiarity: 98 },
  礼: { strokes: 5, tags: ["学识", "温柔"], familiarity: 91 },
  诗: { strokes: 8, tags: ["学识", "温柔"], familiarity: 94 },
  琴: { strokes: 12, tags: ["学识", "温柔"], familiarity: 88 },
  艺: { strokes: 4, tags: ["学识", "开阔"], familiarity: 92 },
  明: { strokes: 8, tags: ["学识", "开阔"], familiarity: 98 },
  朗: { strokes: 10, tags: ["开阔", "勇气"], familiarity: 93 },
  昭: { strokes: 9, tags: ["开阔", "笃定"], familiarity: 82 },
  景: { strokes: 12, tags: ["自然", "开阔"], familiarity: 92 },
  远: { strokes: 7, tags: ["开阔", "勇气"], familiarity: 94 },
  阔: { strokes: 12, tags: ["开阔"], familiarity: 80 },
  宇: { strokes: 6, tags: ["开阔", "笃定"], familiarity: 95 },
  宸: { strokes: 10, tags: ["开阔", "笃定"], familiarity: 86 },
  扬: { strokes: 6, tags: ["开阔", "勇气"], familiarity: 92 },
  航: { strokes: 10, tags: ["开阔", "勇气"], familiarity: 91 },
  越: { strokes: 12, tags: ["开阔", "勇气"], familiarity: 89 },
  毅: { strokes: 15, tags: ["勇气", "笃定"], familiarity: 91 },
  勇: { strokes: 9, tags: ["勇气"], familiarity: 95 },
  卓: { strokes: 8, tags: ["勇气", "笃定"], familiarity: 91 },
  峻: { strokes: 10, tags: ["勇气", "笃定"], familiarity: 85 },
  岩: { strokes: 8, tags: ["自然", "笃定"], familiarity: 88 },
  衡: { strokes: 16, tags: ["笃定", "学识"], familiarity: 82 },
  恒: { strokes: 9, tags: ["笃定"], familiarity: 92 },
  承: { strokes: 8, tags: ["笃定", "勇气"], familiarity: 94 },
  允: { strokes: 4, tags: ["笃定", "温柔"], familiarity: 85 },
  信: { strokes: 9, tags: ["笃定"], familiarity: 96 },
  真: { strokes: 10, tags: ["笃定"], familiarity: 96 },
  新: { strokes: 13, tags: ["开阔", "勇气"], familiarity: 97 },
  生: { strokes: 5, tags: ["自然", "开阔"], familiarity: 98 },
};

export interface CandidateResult {
  name: string;
  fullName: string;
  total: number;
  family: number;
  sound: number;
  glyph: number;
  imagery: number;
  practical: number;
  distinction: number;
  tones: number[];
  pinyin: string[];
  strokes: Array<number | null>;
  imageryTags: ImageryTag[];
  familiarity: number;
  risks: string[];
  strengths: string[];
}

export interface ConsensusReport {
  leader: string;
  state: "共识已形成" | "接近共识" | "需要再谈一轮";
  consensus: number;
  gap: number;
  candidates: CandidateResult[];
  disagreement: string;
  elimination: Array<{ name: string; reason: string }>;
  conversation: string[];
  generatedAt: string;
}

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));
const round = (n: number) => Math.round(n * 10) / 10;
const extractHanChars = (text: string | undefined) => [
  ...new Set((text ?? "").match(/[\u3400-\u9fff]/g) ?? []),
];
const cleanCandidates = (items: string[]) =>
  [
    ...new Set(
      items
        .map((x) => x.trim().replace(/\s+/g, ""))
        .filter((x) => /^[\u3400-\u9fff]{1,2}$/.test(x)),
    ),
  ].slice(0, 5);

function syllables(text: string): string[] {
  return (
    pinyin(text, { toneType: "num", type: "array" }) as unknown as string[]
  ).map((x) => String(x).toLowerCase());
}
function toneOf(py: string): number {
  const m = py.match(/([1-5])$/);
  return m ? Number(m[1]) : 0;
}
function stripTone(py: string): string {
  return py.replace(/[1-5]$/, "");
}

function normalizedWeights(p: SurveyInput["priorities"]) {
  const base: Record<PriorityKey, number> = {
    sound: 18,
    glyph: 14,
    imagery: 18,
    distinction: 10,
  };
  const raw = (Object.keys(base) as PriorityKey[]).map((k) => ({
    k,
    v: base[k] * (0.8 + 0.1 * clamp(p[k], 1, 5)),
  }));
  const scale = 60 / raw.reduce((s, x) => s + x.v, 0);
  return Object.fromEntries(raw.map((x) => [x.k, x.v * scale])) as Record<
    PriorityKey,
    number
  >;
}

export function buildReport(source: SurveyInput): ConsensusReport {
  const names = cleanCandidates(source.candidates);
  if (names.length < 2) throw new Error("请至少提供 2 个有效候选名");
  const input = { ...source, candidates: names };
  const weights = normalizedWeights(input.priorities);
  const results = names.map((name, index) =>
    scoreCandidate(input, name, index, weights),
  );
  results.sort(
    (a, b) =>
      b.total - a.total ||
      b.family - a.family ||
      b.practical - a.practical ||
      b.sound - a.sound ||
      names.indexOf(a.name) - names.indexOf(b.name),
  );
  const gap = round(results[0].total - (results[1]?.total ?? 0));
  const hasReservation = input.votes.some(
    (v) => v.reservation === results[0].name,
  );
  const state =
    gap >= 8 && !hasReservation
      ? "共识已形成"
      : gap >= 3 && !hasReservation
        ? "接近共识"
        : "需要再谈一轮";
  const uniqueFavorites = new Set(
    input.votes.map((v) => v.favorite).filter(Boolean),
  );
  const disagreement =
    uniqueFavorites.size <= 1
      ? "三方首选高度一致，当前分歧主要是风险核对。"
      : uniqueFavorites.size === 2
        ? "分歧集中在两位候选之间，适合只比较各自最在意的一项。"
        : "三方首选分散，说明大家正在用不同标准讨论同一件事。";
  const elimination = results
    .slice(1)
    .reverse()
    .map((r) => {
      const dims: Array<[string, number]> = [
        ["家庭支持", r.family / 24],
        ["日常实用", r.practical / 16],
        ["音律", r.sound / weights.sound],
        ["字形", r.glyph / weights.glyph],
        ["意象", r.imagery / weights.imagery],
      ];
      dims.sort((a, b) => a[1] - b[1]);
      return {
        name: r.name,
        reason: `主要落后在${dims[0][0]}；${r.risks[0] ?? "与第一名的综合差距更大"}`,
      };
    });
  const conversation = [
    `先只谈「${results[0].name}」和「${results[1].name}」，每人说一个最喜欢的具体场景，不评价对方审美。`,
    hasReservation
      ? `第一名仍被列为保留项，请提出一个可验证的担忧：读错、写错、谐音，还是家族避讳？`
      : `把前三名分别连读三次，并模拟学校点名与电话报名字。`,
    `约定最终决定前一起核对户籍可用字、方言谐音和直系长辈避讳。`,
  ];
  return {
    leader: results[0].name,
    state,
    consensus: clamp(
      Math.round(
        54 +
          gap * 4 +
          (uniqueFavorites.size === 1 ? 18 : 0) -
          (hasReservation ? 16 : 0),
      ),
      32,
      96,
    ),
    gap,
    candidates: results,
    disagreement,
    elimination,
    conversation,
    generatedAt: new Date().toISOString(),
  };
}

function scoreCandidate(
  input: SurveyInput,
  name: string,
  index: number,
  weights: Record<PriorityKey, number>,
): CandidateResult {
  const fullName = input.surname + name;
  const py = syllables(fullName);
  const tones = py.map(toneOf);
  const chars = [...name];
  const metas = chars.map((ch) => H[ch]);
  const strokes = metas.map((m) => m?.strokes ?? null);
  const known = metas.filter(Boolean) as HanziMeta[];
  const risks: string[] = [];
  const strengths: string[] = [];
  const forbidden = extractHanChars(input.forbiddenChars);
  const preferred = extractHanChars(input.preferredChars).filter(
    (char) => !forbidden.includes(char),
  );
  const preferredMatches = chars.filter((char) => preferred.includes(char));

  let familyNet = 0;
  for (const vote of input.votes) {
    familyNet +=
      vote.favorite === name ? 3 : vote.reservation === name ? -2 : 1;
  }
  familyNet += Math.min(preferredMatches.length * 1.5, 3);
  const family = clamp(((familyNet + 6) / 15) * 24, 0, 24);
  if (family >= 19) strengths.push("家人支持集中");
  if (preferredMatches.length)
    strengths.push(`包含家族希望字“${preferredMatches.join("、")}”`);
  if (input.votes.some((v) => v.reservation === name))
    risks.push("有家人把它列为保留项");

  let soundRaw = 10;
  const transitions = tones
    .slice(1)
    .filter((t, i) => t && tones[i] && t !== tones[i]).length;
  soundRaw += transitions * 2.3;
  if (new Set(tones.filter(Boolean)).size >= 3) soundRaw += 2;
  if (tones.length >= 3 && tones.every((t) => t === tones[0])) {
    soundRaw -= 6;
    risks.push("声调起伏较少，建议连读复核");
  }
  const bare = py.map(stripTone);
  if (new Set(bare).size < bare.length) {
    soundRaw -= 2;
    risks.push("相邻音节较接近");
  }
  const sound = clamp((soundRaw / 18) * weights.sound, 0, weights.sound);
  if (sound / weights.sound >= 0.78) strengths.push("朗读节奏有起伏");

  let glyphRaw = 9;
  if (strokes.every((x) => x !== null)) {
    const nums = strokes as number[];
    const total = nums.reduce((a, b) => a + b, 0);
    const diff = nums.length === 2 ? Math.abs(nums[0] - nums[1]) : 0;
    glyphRaw += diff <= 5 ? 3 : diff <= 9 ? 1 : -2;
    glyphRaw += total >= 12 && total <= 32 ? 2 : total > 38 ? -3 : 0;
    if (total > 34) risks.push("连续书写负担偏高");
  } else {
    glyphRaw -= 2;
    risks.push("含未收录字，笔画与字义需人工核对");
  }
  if (chars.length === 2 && chars[0] === chars[1]) {
    glyphRaw -= 3;
    risks.push("叠字辨识需结合使用场景");
  }
  const glyph = clamp((glyphRaw / 14) * weights.glyph, 0, weights.glyph);
  if (glyph / weights.glyph >= 0.76) strengths.push("字形负担较均衡");

  const tags = [...new Set(known.flatMap((m) => m.tags))] as ImageryTag[];
  const matches = tags.filter((t) => input.imagery.includes(t)).length;
  const imageryRaw = clamp(7 + matches * 4 + (tags.length >= 3 ? 2 : 0), 0, 18);
  const imagery = (imageryRaw / 18) * weights.imagery;
  if (matches > 0)
    strengths.push(
      `呼应${tags.filter((t) => input.imagery.includes(t)).join("、")}意象`,
    );

  let practicalRaw = 16;
  if (chars.some((c) => forbidden.includes(c))) {
    practicalRaw -= 8;
    risks.push("命中你填写的家族避讳/禁用字");
  }
  if (known.length < chars.length)
    practicalRaw -=
      input.rareTolerance === "low"
        ? 5
        : input.rareTolerance === "medium"
          ? 3
          : 1;
  if (name.length !== 2) {
    practicalRaw -= 1;
    risks.push("单字名请额外关注重名与口述辨识");
  }
  if (input.riskStrictness === "high" && new Set(bare).size < bare.length)
    practicalRaw -= 2;
  const practical = clamp(practicalRaw, 0, 16);
  if (practical >= 14) strengths.push("日常使用风险较少");

  const familiarity = known.length
    ? known.reduce((s, m) => s + m.familiarity, 0) / known.length
    : 48;
  const target =
    input.duplicateSensitivity === "high"
      ? 70
      : input.duplicateSensitivity === "medium"
        ? 78
        : 86;
  const distinctionRaw = clamp(10 - Math.abs(familiarity - target) / 6, 2, 10);
  const distinction = (distinctionRaw / 10) * weights.distinction;
  if (familiarity >= 62 && familiarity <= 86)
    strengths.push("熟悉感与辨识度相对平衡");
  if (familiarity > 93 && input.duplicateSensitivity === "high")
    risks.push("常见字组合，建议另查真实重名数据");

  const total = round(
    family + sound + glyph + imagery + practical + distinction,
  );
  return {
    name,
    fullName,
    total,
    family: round(family),
    sound: round(sound),
    glyph: round(glyph),
    imagery: round(imagery),
    practical: round(practical),
    distinction: round(distinction),
    tones,
    pinyin: py,
    strokes,
    imageryTags: tags,
    familiarity: round(familiarity),
    risks: [...new Set(risks)],
    strengths: [...new Set(strengths)].slice(0, 4),
  };
}

export const defaultSurvey: SurveyInput = {
  surname: "林",
  candidates: ["知夏", "砚舟", "清和", "予安"],
  votes: [
    { role: "妈妈", favorite: "知夏", reservation: "砚舟" },
    { role: "爸爸", favorite: "清和", reservation: "知夏" },
    { role: "长辈代表", favorite: "予安", reservation: "砚舟" },
  ],
  priorities: { sound: 5, glyph: 3, imagery: 5, distinction: 4 },
  imagery: ["自然", "学识", "温柔"],
  forbiddenChars: "",
  preferredChars: "",
  rareTolerance: "medium",
  duplicateSensitivity: "high",
  riskStrictness: "high",
};
